// Epic error scanner — pulls per-session messages for the Prisma task
// bots (ScheduleNewAppointment, AppointmentActions, MyChartMessage) and
// extracts Epic-side failure signals from each tool invocation.
//
// WHY this lives in its own lib:
//   The classifier is tenant-agnostic — give it a list of target bot
//   names, an agent-id→name map, and a message-fetch callback, and it
//   emits a per-bot/per-tool/per-error-type invocation count. The UI
//   shell handles rate-limiting + progress + cancellation; the scanner
//   just reads tool metadata.
//
// SIGNALS consumed on each tool invocation (shape verified in prod
// 2026-10-23 via wxcc-operations-get-ai-agent-session-messages):
//   metadata.tools[].tool_name          — Agent Studio tool name
//   metadata.tools[].state              — "SUCCESS" when fulfillment OK;
//                                         anything else is a tool-side
//                                         failure the bot had to react to
//   metadata.tools[].output             — stringified response body;
//                                         Epic error codes land here as
//                                         plain substrings (e.g.
//                                         "INVALID-DATE" / "NO-MYCHART-FOUND")
//
// Error-type taxonomy (matched against output + state verbatim):
//   INVALID-DATE          — Epic Scheduling2017 window-cap breach (>30d
//                           in prod). The bot's +60d retry pattern
//                           always hits this.
//   INVALID-START-DATE    — Epic lead-time rule (<today + 3d).
//   INVALID-PROVIDER-ID   — Provider payload malformed (common cause:
//                           LLM re-serialised a padded Internal ID and
//                           stripped the leading whitespace).
//   INVALID-VISIT-TYPE    — VisitType payload bad / stripped padding.
//   INVALID-DEPARTMENT    — Department payload bad.
//   NO-MYCHART-FOUND      — Patient has no MyChart; /future returns
//                           empty; expected for ~15-20% of callers.
//   non-SUCCESS state     — any tool state !== "SUCCESS" that didn't
//                           match one of the specific codes above.
//                           Catches middleware timeouts, 5xx, etc.
//
// Output count semantics: per-INVOCATION, not per-session. One session
// can retry the same tool 3× and generate 3 INVALID-DATE hits — a
// supervisor usually wants the invocation count (the retry rate matters).
// A `sessionIdsByError` map carries session de-dup for drill-through.

// Target bots this scanner looks at. Caller can pass its own list but
// the Prisma Ortho defaults are the ones you want 95% of the time.
export const DEFAULT_TARGET_BOTS = [
  'PROD-Ortho_ScheduleNewAppointment',
  'PROD-Prisma_AppointmentActions',
  'PROD-Prisma_MyChartMessage',
];

// Specific error codes we look for in tool `output` fields. Case-sensitive —
// Epic returns these in SCREAMING-KEBAB-CASE verbatim so we match them that way.
export const EPIC_ERROR_CODES = [
  'INVALID-DATE',
  'INVALID-START-DATE',
  'INVALID-PROVIDER-ID',
  'INVALID-VISIT-TYPE',
  'INVALID-DEPARTMENT',
  'NO-MYCHART-FOUND',
];

// The catch-all bucket when state !== SUCCESS but no specific code matched.
const OTHER_ERROR = 'OTHER (non-SUCCESS state)';

export const ALL_ERROR_TYPES = [...EPIC_ERROR_CODES, OTHER_ERROR];

/**
 * Scan a list of sessions for Epic errors.
 *
 * @param {Array} sessions - contactAIAgentSession rows (current window)
 * @param {object} opts - {
 *   targetBots?: string[],                           // defaults to Prisma task bots
 *   agentIdByName: { [botName]: agentId },           // for the fetch call
 *   agentIdToName?: { [agentId]: botName },          // empty-agentName fallback
 *   fetchMessages: async (agentId, sessionId) => { messages: [] },
 *   onProgress?: (done, total) => void,
 *   signal?: AbortSignal,
 * }
 * @returns {Promise<{
 *   totals: { [bot]: { [tool]: { [errorType]: number } } },
 *   invocationCounts: { [bot]: { [tool]: number } },  // all invocations (success + failure)
 *   sessionIdsByError: { [bot]: { [tool]: { [errorType]: Set<string> } } },
 *   done: number,
 *   total: number,
 * }>}
 */
export async function scanEpicErrors(sessions, opts) {
  const targetBots = new Set(opts.targetBots ?? DEFAULT_TARGET_BOTS);
  const agentIdToName = opts.agentIdToName ?? {};
  const resolveBotName = (s) =>
    s.agentName || (s.agentId && agentIdToName[s.agentId]) || '';

  const totals = {};              // bot → tool → errorType → count
  const invocationCounts = {};    // bot → tool → total invocation count
  const sessionIdsByError = {};   // bot → tool → errorType → Set<sessionId>

  const inc = (bot, tool, errorType, sessionId) => {
    totals[bot] ??= {};
    totals[bot][tool] ??= {};
    totals[bot][tool][errorType] = (totals[bot][tool][errorType] ?? 0) + 1;
    sessionIdsByError[bot] ??= {};
    sessionIdsByError[bot][tool] ??= {};
    sessionIdsByError[bot][tool][errorType] ??= new Set();
    if (sessionId) sessionIdsByError[bot][tool][errorType].add(sessionId);
  };

  const bumpInvocation = (bot, tool) => {
    invocationCounts[bot] ??= {};
    invocationCounts[bot][tool] = (invocationCounts[bot][tool] ?? 0) + 1;
  };

  const targetSessions = sessions.filter((s) => targetBots.has(resolveBotName(s)));
  let done = 0;

  for (const s of targetSessions) {
    if (opts.signal?.aborted) break;
    const botName = resolveBotName(s);
    const agentId = opts.agentIdByName?.[botName];
    if (!agentId) {
      done += 1;
      opts.onProgress?.(done, targetSessions.length);
      continue;
    }

    let messages = [];
    try {
      const res = await opts.fetchMessages(agentId, s.sessionId);
      messages = res?.messages ?? [];
    } catch {
      messages = [];
    }

    for (const m of messages) {
      const tools = m?.metadata?.tools ?? [];
      for (const t of tools) {
        const toolName = t.tool_name ?? t.name ?? '(unknown)';
        bumpInvocation(botName, toolName);

        // Serialise output + state to a single haystack for substring match.
        // Epic error codes sometimes land in `state`, sometimes in `output`,
        // sometimes in a nested JSON blob — stringifying once catches all.
        let outputStr = '';
        if (t.output != null) {
          outputStr = typeof t.output === 'string' ? t.output : safeStringify(t.output);
        }
        const stateStr = t.state ?? '';
        const haystack = `${stateStr}\n${outputStr}`;

        let hitSpecific = false;
        for (const code of EPIC_ERROR_CODES) {
          if (haystack.includes(code)) {
            inc(botName, toolName, code, s.sessionId);
            hitSpecific = true;
          }
        }
        // Only count OTHER when state !== SUCCESS and no specific code already
        // claimed the invocation. (A state=SUCCESS tool with INVALID-DATE in
        // the output is still a success from the bot's perspective — the Epic
        // error was returned as a soft failure. We count it under its specific
        // code only, not under OTHER.)
        if (!hitSpecific && stateStr && stateStr !== 'SUCCESS') {
          inc(botName, toolName, OTHER_ERROR, s.sessionId);
        }
      }
    }

    done += 1;
    opts.onProgress?.(done, targetSessions.length);
  }

  return {
    totals,
    invocationCounts,
    sessionIdsByError,
    done,
    total: targetSessions.length,
  };
}

/**
 * Flatten the scanner's totals into a per-row shape the ReportTable can
 * render. One row per (bot, tool, errorType) combination with >=1 count.
 */
export function toRows(result) {
  const rows = [];
  const totals = result?.totals ?? {};
  const invocations = result?.invocationCounts ?? {};
  const sessionsByError = result?.sessionIdsByError ?? {};

  for (const [bot, toolMap] of Object.entries(totals)) {
    for (const [tool, errorMap] of Object.entries(toolMap)) {
      const totalInvocations = invocations?.[bot]?.[tool] ?? 0;
      for (const [errorType, count] of Object.entries(errorMap)) {
        const pct = totalInvocations > 0 ? Math.round((count / totalInvocations) * 100) : 0;
        const sessionIds = [...(sessionsByError?.[bot]?.[tool]?.[errorType] ?? new Set())];
        rows.push({
          key: `${bot}|${tool}|${errorType}`,
          bot,
          tool,
          errorType,
          count,
          totalInvocations,
          pct,
          sessionIds,
        });
      }
    }
  }
  return rows.sort((a, b) => b.count - a.count);
}

function safeStringify(obj) {
  try { return JSON.stringify(obj); } catch { return String(obj); }
}
